import { useCallback } from 'react';
import EChartsReactCore from 'echarts-for-react/lib/core';
import { useChartStoreSeriesActions } from './context';
import { LegendItem } from './types';

type UseLegendInteractionsParams = {
    getEchartsInstance: () => ReturnType<EChartsReactCore['getEchartsInstance']> | undefined;
    legendItems: LegendItem[];
};

export function useLegendInteractions({ getEchartsInstance, legendItems }: UseLegendInteractionsParams) {
    const { getHiddenSeries, toggleSeriesVisibility, isolateSeriesToggle } = useChartStoreSeriesActions();

    const toggleSeries = useCallback(
        (seriesName: string) => {
            // Clear any active highlight on this series *before* toggling visibility.
            // Hovering a legend chip dispatches `highlight`, which puts every other
            // series into `blur` state via `emphasis.focus: 'series'`. Normally that
            // blur is lifted by the matching `downplay` from `handleLegendMouseLeave`,
            // but on click we hide the series and rebuild the option with it removed
            // — after that, dispatching `downplay` for a series that no longer exists
            // is a no-op, so the other series would stay stuck in blur. Downplaying
            // here, while the series is still in the option, lifts the blur cleanly
            // before the rebuild.
            const instance = getEchartsInstance();
            if (instance) {
                instance.dispatchAction({ type: 'downplay', seriesName });
            }
            toggleSeriesVisibility(seriesName);
        },
        [getEchartsInstance, toggleSeriesVisibility],
    );

    const isolateSeries = useCallback(
        (seriesName: string) => {
            const allIds = legendItems.map((item) => item.id);
            isolateSeriesToggle(seriesName, allIds);
        },
        [legendItems, isolateSeriesToggle],
    );

    const handleLegendMouseEnter = useCallback(
        (seriesName: string) => {
            if (getHiddenSeries().has(seriesName)) {
                return;
            }
            const instance = getEchartsInstance();
            if (instance) {
                instance.dispatchAction({ type: 'highlight', seriesName });
            }
        },
        [getEchartsInstance, getHiddenSeries],
    );

    const handleLegendMouseLeave = useCallback(
        (seriesName: string) => {
            if (getHiddenSeries().has(seriesName)) {
                return;
            }
            const instance = getEchartsInstance();
            if (instance) {
                instance.dispatchAction({ type: 'downplay', seriesName });
            }
        },
        [getEchartsInstance, getHiddenSeries],
    );

    return { toggleSeries, isolateSeries, handleLegendMouseEnter, handleLegendMouseLeave };
}
